import { Card } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CARD_HEIGHT, CARD_WIDTH, cardTopY, type LayoutEntry } from './layout-events'
import type { TimelineCardEvent } from './TimelineEventCard'

/** Shape passed to the per-member click handler. Keeps the surface
 *  identical to TimelineEventCard's onSelectEdge — so the parent can
 *  wire both to the same selection store with no special-casing. */
export interface TimelineClusterBadgeProps {
  /** Layout entry produced by ``layoutEvents`` — guaranteed ``kind === 'cluster'``. */
  layout: Extract<LayoutEntry, { kind: 'cluster' }>
  axisY: number
  /** Members in input order. Already projected to card-event shape so the
   *  popover can render each row consistently with the rest of the timeline. */
  members: readonly TimelineCardEvent[]
  /** Controlled open state. Lives on the parent so a viewport change (pan /
   *  zoom) can force-collapse the cluster. */
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** Per-member click: should mirror TimelineEventCard.onSelectEdge so the
   *  selection store / sidebar see the same shape as a normal event click. */
  onSelectMember: (timelineItemId: string, anchor: HTMLElement) => void
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatTimestamp(ts: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  const month = MONTHS[d.getMonth()]
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${month} ${day}, ${year} · ${hh}:${mm}`
}

export function TimelineClusterBadge({
  layout,
  axisY,
  members,
  isOpen,
  onOpenChange,
  onSelectMember,
}: TimelineClusterBadgeProps) {
  const top = cardTopY(axisY, layout)
  const left = layout.x - CARD_WIDTH / 2
  const count = layout.memberIds.length

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-event-card
          onMouseDown={e => e.stopPropagation()}
          title={`${count} events at ${formatTimestamp(layout.timestamp)}`}
          // The `before` box widens the 24 px pill to a 40 px tap target on a
          // phone without moving it off its leader line.
          className="absolute flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-left text-xs font-medium text-primary shadow-sm transition-colors hover:bg-primary/20 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none max-md:before:absolute max-md:before:inset-x-0 max-md:before:-inset-y-2"
          style={{
            left,
            top: top + (CARD_HEIGHT - 24) / 2,
            width: CARD_WIDTH,
            height: 24,
          }}
        >
          <span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-primary text-3xs font-bold tabular-nums text-primary-foreground">
            {count > 99 ? '99+' : count}
          </span>
          <span className="truncate">events · {formatTimestamp(layout.timestamp)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side={layout.lane === 'above' ? 'top' : 'bottom'}
        sideOffset={8}
        // Width is independent of the trigger badge width (which matches
        // CARD_WIDTH, 212 px — too narrow for entity labels + relation
        // chips). We use a sensible fixed width that fits typical content
        // and only shrink on small viewports.
        className="w-[28rem] max-w-[calc(100vw-2rem)] p-0"
        // Don't steal focus when clicking inside the list — keeps the user's
        // selection / keyboard focus on whatever they had before. Also keeps
        // clicks on individual rows from collapsing the popover so the user
        // can browse the cluster while the side panel updates.
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className="overflow-hidden border-b border-border px-3 py-2 text-2xs font-medium text-muted-foreground">
          <span className="block truncate">
            {count} events · {formatTimestamp(layout.timestamp)}
          </span>
        </div>
        {/* ``overflow-x-hidden`` is explicit on purpose: when overflow-y is
            ``auto`` the spec promotes ``overflow-x: visible`` to ``auto`` as
            well, so a single over-wide row (a raw element-id leaking through
            ``truncate``, say) was producing a horizontal scrollbar across
            the whole list. Forcing x to hidden clips that overflow cleanly
            and the inner ``truncate`` cells render their own ellipsis.
            Spacing lives on this parent (``space-y-1`` + ``p-1``) rather
            than on each Card (``m-1``) because Card uses ``asChild`` to
            merge its classes onto the inner ``w-full`` button — a margin
            on a ``w-full`` element overflows the parent by the margin
            width on each side, which combined with ``overflow-x-hidden``
            clipped the right edge invisibly. */}
        <div className="max-h-[60vh] space-y-1 overflow-x-hidden overflow-y-auto p-1">
          {members.map(ev => (
            <Card
              key={ev.id}
              asChild
              size="compact"
              interactive
              className="min-w-0 rounded-md border-transparent shadow-none hover:border-border"
            >
              <button
                type="button"
                onClick={e => onSelectMember(ev.id, e.currentTarget)}
                className="flex w-full min-w-0 items-center gap-2 px-2 py-1.5 text-left max-md:py-2.5"
              >
                <span className="inline-flex h-4 min-w-[2rem] shrink-0 items-center justify-center rounded-sm border border-border/60 bg-background/60 px-1 font-mono text-4xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {ev.sourceGlyph}
                </span>
                <span className="block min-w-0 flex-1 truncate text-xs text-foreground">
                  {ev.sourceName}
                  {ev.targetName ? (
                    <span className="text-muted-foreground"> → {ev.targetName}</span>
                  ) : null}
                </span>
                <span className="shrink-0 font-mono text-3xs uppercase tracking-wider text-primary/80">
                  {ev.type.replace(/_/g, ' ')}
                </span>
              </button>
            </Card>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
